import { useState, useEffect } from "react";
import { AIR_BRAKE_INSPECTION_STEPS, INSPECTION_MODES } from "../data/airBrakeInspectionChecklist";
import "./AirBrakeInspector.css";

export default function AirBrakeInspector({ onHome }) {
  const [mode, setMode] = useState(null);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [stepResults, setStepResults] = useState({});
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [sessionScore, setSessionScore] = useState(null);

  const steps = AIR_BRAKE_INSPECTION_STEPS;
  const currentStep = steps[currentStepIdx];

  useEffect(() => {
    if (!isTimerRunning) return;
    const interval = setInterval(() => setTimer(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const handleStartMode = (selectedMode) => {
    setMode(selectedMode);
    setCurrentStepIdx(0);
    setStepResults({});
    setTimer(0);
    setIsTimerRunning(selectedMode === INSPECTION_MODES.PRACTICE);
  };

  const handleStepComplete = (stepId, passed) => {
    setStepResults({ ...stepResults, [stepId]: passed });
    if (currentStepIdx < steps.length - 1) {
      setCurrentStepIdx(currentStepIdx + 1);
    } else {
      finishInspection();
    }
  };

  const finishInspection = () => {
    setIsTimerRunning(false);
    const totalSteps = steps.length;
    const passedSteps = Object.values(stepResults).filter(p => p).length;
    const score = Math.round((passedSteps / totalSteps) * 100);
    setSessionScore({ passed: passedSteps, total: totalSteps, score });
  };

  const resetSession = () => {
    setMode(null);
    setCurrentStepIdx(0);
    setStepResults({});
    setTimer(0);
    setIsTimerRunning(false);
    setSessionScore(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!mode) {
    return (
      <div className="air-brake-inspector">
        <header className="inspector-header">
          <button className="back-btn" onClick={onHome}>← Back</button>
          <h1>🔧 Air Brake Inspector Training</h1>
        </header>

        <div className="mode-selection">
          <p>Choose how you'd like to practice:</p>

          <button
            className="mode-card guided"
            onClick={() => handleStartMode(INSPECTION_MODES.GUIDED)}
          >
            <div className="mode-icon">📋</div>
            <h2>Guided Mode</h2>
            <p>Step-by-step walkthrough with detailed instructions and safety tips. Perfect for learning.</p>
            <span className="btn-action">Start Guided Tour →</span>
          </button>

          <button
            className="mode-card practice"
            onClick={() => handleStartMode(INSPECTION_MODES.PRACTICE)}
          >
            <div className="mode-icon">⏱️</div>
            <h2>Practice Mode</h2>
            <p>Complete a full inspection under time pressure. Track your performance and identify weak areas.</p>
            <span className="btn-action">Start Practice →</span>
          </button>

          <button
            className="mode-card quiz"
            onClick={() => handleStartMode(INSPECTION_MODES.QUIZ)}
          >
            <div className="mode-icon">❓</div>
            <h2>Quiz Mode</h2>
            <p>Answer questions about inspection procedures and troubleshooting scenarios.</p>
            <span className="btn-action">Start Quiz →</span>
          </button>
        </div>
      </div>
    );
  }

  if (sessionScore) {
    return (
      <div className="air-brake-inspector">
        <header className="inspector-header">
          <h1>Inspection Complete</h1>
        </header>

        <div className="results-container">
          <div className="score-display">
            <div className={`score-circle ${sessionScore.score >= 80 ? "pass" : "fail"}`}>
              <span className="score-number">{sessionScore.score}%</span>
            </div>
            <h2>{sessionScore.score >= 80 ? "✓ Passed" : "✗ Needs Review"}</h2>
            <p>
              {sessionScore.passed} of {sessionScore.total} checks completed successfully
              {mode === INSPECTION_MODES.PRACTICE && ` in ${formatTime(timer)}`}
            </p>
          </div>

          <div className="results-breakdown">
            <h3>Inspection Summary</h3>
            {steps.map(step => (
              <div key={step.id} className="result-item">
                <span className={stepResults[step.id] ? "pass-icon" : "fail-icon"}>
                  {stepResults[step.id] ? "✓" : "✗"}
                </span>
                <div>
                  <strong>{step.title}</strong>
                  <small>{step.category}</small>
                </div>
              </div>
            ))}
          </div>

          <div className="action-buttons">
            <button className="btn btn-retry" onClick={resetSession}>
              Try Again
            </button>
            <button className="btn btn-home" onClick={onHome}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="air-brake-inspector">
      <header className="inspector-header">
        <button className="back-btn" onClick={resetSession}>← Back</button>
        <h1>{mode === INSPECTION_MODES.GUIDED ? "📋 Guided Tour" : mode === INSPECTION_MODES.PRACTICE ? "⏱️ Practice Mode" : "❓ Quiz"}</h1>
        {mode === INSPECTION_MODES.PRACTICE && (
          <div className="timer">{formatTime(timer)}</div>
        )}
      </header>

      <div className="inspection-content">
        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar-bg">
            <div className="progress-bar-fill" style={{width: `${((currentStepIdx) / steps.length) * 100}%`}}></div>
          </div>
          <p className="progress-text">Step {currentStepIdx + 1} of {steps.length}</p>
        </div>

        {/* Step Content */}
        <div className="step-card">
          <div className="step-header">
            <span className="step-category">{currentStep.category}</span>
            <h2>{currentStep.title}</h2>
          </div>

          <p className="step-description">{currentStep.description}</p>

          {/* Steps List */}
          <div className="steps-list">
            <h3>What to Check:</h3>
            <ul>
              {currentStep.steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Readings/Info Box */}
          {currentStep.readings && (
            <div className="readings-box">
              <h3>📊 Expected Readings:</h3>
              <div className="readings-content">
                {Object.entries(currentStep.readings).map(([key, value]) => (
                  <div key={key} className="reading-item">
                    <span className="reading-label">{key.replace(/([A-Z])/g, " $1").trim()}:</span>
                    <span className="reading-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Safety Tip */}
          <div className="safety-box">
            <span className="safety-icon">⚠️</span>
            <div>
              <strong>Safety Tip:</strong>
              <p>{currentStep.safetyTip}</p>
            </div>
          </div>

          {/* Pass Criteria */}
          <div className="pass-criteria-box">
            <strong>✓ This step passes if:</strong>
            <p>{currentStep.passCriteria}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="step-actions">
          <button
            className="btn btn-pass"
            onClick={() => handleStepComplete(currentStep.id, true)}
          >
            ✓ This Check Passed
          </button>
          <button
            className="btn btn-fail"
            onClick={() => handleStepComplete(currentStep.id, false)}
          >
            ✗ This Check Failed
          </button>
        </div>
      </div>
    </div>
  );
}
